/**
 * Reactive, safe signals: observable values, derived computeds, and effects
 * that track their dependencies and clean up automatically.
 *
 * @module
 */
import { Instant, Nano, now, Second, sleep, sleepUntil } from "@moq/clock";

/** Cancels a subscription, effect, or other registration when called. */
export type Dispose = () => void;

const noop: Dispose = () => {};

/** Receives a value each time it changes. */
export type Subscriber<T> = (value: T) => void;

// Symbols to identify our instances across different package versions.
/** Marks a {@link Signal}, even one from another copy of this package; it implies a write side. */
const SIGNAL_BRAND = Symbol.for("@moq/signals");
/** Marks every readable this package ships, even one from another copy of it. */
const GETTER_BRAND = Symbol.for("@moq/signals.getter");
const CHANGE_LISTEN = Symbol.for("@moq/signals.change.listen");

function branded(value: unknown, brand: symbol): boolean {
	return typeof value === "object" && value !== null && brand in value;
}

/** Read side of a signal: peek the current value and subscribe to changes. */
export interface Getter<T> {
	/** Returns the current value without subscribing. */
	peek(): T;

	/** Resolves with the value the next time it changes. */
	changed(): Promise<T>;
	/** Calls `fn` once the next time the value changes. Returns a function to cancel. */
	changed(fn: Subscriber<T>): Dispose;

	/** Calls `fn` every time the value changes. */
	subscribe(fn: Subscriber<T>): Dispose;
}

/** Write side of a signal: replace or transform the current value. */
export interface Setter<T> {
	/** Replaces the value. A function is stored as-is; use {@link update} to transform. */
	set(value: T): void;
	/** Transforms the value via a function of the previous value. */
	update(fn: (prev: T) => T): void;
}

/**
 * The read side of a {@link Once}: observe it reactively ({@link Getter}, `undefined` while
 * pending) or await it (a `PromiseLike` that resolves with the settled value, immediately if it
 * already settled). Expose this to callers so they can peek/observe/await but not settle it.
 */
export interface GetPromise<T> extends Getter<T | undefined>, PromiseLike<T> {}

/** A mutable observable value. Writes are coalesced per microtask and only notify subscribers when the value actually changes. */
export class Signal<T> implements Getter<T>, Setter<T> {
	#value: T;

	#subscribers: Set<Subscriber<T>> = new Set();
	#changed: Set<Subscriber<T>> = new Set();

	// Microtask coalescing state
	#pending = false;
	#oldValue: T | undefined;
	#hasCapturedOldValue = false;
	#forceNotify = false;

	/** Marks this as a Signal across package instances. */
	readonly [SIGNAL_BRAND] = true;
	/** Marks this as a readable across package instances. */
	readonly [GETTER_BRAND] = true;

	constructor(value: T) {
		this.#value = value;
	}

	/** Returns the value if it's already a Signal, otherwise wraps it in a new Signal. */
	static from<T>(value: T | Signal<T>): Signal<T> {
		// Use brand check instead of instanceof to work across package instances
		if (branded(value, SIGNAL_BRAND)) {
			return value as Signal<T>;
		}
		return new Signal(value as T);
	}

	/** Returns the current value without subscribing. */
	peek(): T {
		return this.#value;
	}

	/**
	 * Sets the current value, notifying subscribers if it changed.
	 * Pass `notify` true to always notify or false to never notify.
	 * A function is stored as the value; use {@link update} to transform instead.
	 */
	set(value: T, notify?: boolean): void {
		// Capture old value before the first set in this microtask.
		if (!this.#hasCapturedOldValue) {
			this.#oldValue = this.#value;
			this.#hasCapturedOldValue = true;
		}

		this.#value = value;

		// If notify is false, don't notify.
		if (notify === false) return;

		if (notify === true) this.#forceNotify = true;

		// If there are no subscribers, don't queue a microtask.
		// Drop the capture since no listener saw the old value; a flush already queued finds
		// nothing to deliver.
		if (this.#subscribers.size === 0 && this.#changed.size === 0) {
			this.#hasCapturedOldValue = false;
			this.#oldValue = undefined;
			this.#forceNotify = false;
			return;
		}

		// Coalesce multiple set() calls into a single microtask.
		if (this.#pending) return;
		this.#pending = true;

		queueMicrotask(() => this.#flush());
	}

	#flush(): void {
		this.#pending = false;

		// A write while nobody listened dropped the capture, so any listener since then saw the
		// current value when it subscribed and there is no change to deliver.
		if (!this.#hasCapturedOldValue) return;
		this.#hasCapturedOldValue = false;
		const old = this.#oldValue;
		this.#oldValue = undefined;

		const force = this.#forceNotify;
		this.#forceNotify = false;

		// Check if the net change is zero (value returned to what it was before).
		if (!force && isEqual(old as T, this.#value)) return;

		const value = this.#value;
		const changed = this.#changed;
		this.#changed = new Set();

		for (const fn of this.#subscribers) {
			try {
				fn(value);
			} catch (error) {
				console.error("signal subscriber error", error);
			}
		}

		for (const fn of changed) {
			try {
				fn(value);
			} catch (error) {
				console.error("signal changed error", error);
			}
		}
	}

	/** Sets the value to the result of `fn(prev)`, notifying subscribers unless `notify` is false. */
	update(fn: (prev: T) => T, notify = true): void {
		const value = fn(this.#value);
		this.set(value, notify);
	}

	/**
	 * Mutates the current value in place via `fn`, returning `fn`'s result and
	 * notifying subscribers unless `notify` is false.
	 */
	mutate<R>(fn: (value: T) => R, notify = true): R {
		const r = fn(this.#value);
		this.set(this.#value, notify);
		return r;
	}

	/** Calls `fn` every time the value changes. Returns a function to unsubscribe. */
	subscribe(fn: Subscriber<T>): Dispose {
		this.#subscribers.add(fn);
		return () => this.#subscribers.delete(fn);
	}

	/** Resolves with the value the next time it changes. */
	changed(): Promise<T>;
	/** Calls `fn` once the next time the value changes. Returns a function to cancel. */
	changed(fn: Subscriber<T>): Dispose;
	changed(fn?: Subscriber<T>): Promise<T> | Dispose {
		if (fn) {
			this.#changed.add(fn);
			return () => this.#changed.delete(fn);
		}
		return new Promise<T>((resolve) => {
			this.#changed.add(resolve);
		});
	}

	/** Calls `fn` with the current value now, and again every time it changes. */
	watch(fn: Subscriber<T>): Dispose {
		const dispose = this.subscribe(fn);
		queueMicrotask(() => fn(this.#value));
		return dispose;
	}

	/**
	 * Waits lazily for the next change, releasing listeners when it settles or loses a race.
	 * Use {@link race} or {@link Effect.race} to cancel a losing wait; direct `await` also works.
	 */
	static race<T extends readonly unknown[]>(
		...sigs: { [K in keyof T]: Getter<T[K]> }
	): GetPromise<Awaited<T[number]>> {
		return new Changed<T[number]>(sigs);
	}
}

// A shared, lazy wait for one change. Unlike Once, a change can settle with undefined,
// so keep a separate settled flag and let listen() observe that distinction.
class Changed<T> implements GetPromise<Awaited<T>> {
	#sources: readonly Getter<T>[];
	#listeners = new Set<Listener>();
	#dispose: Dispose[] = [];
	#won = false;
	#result?: Settled;

	constructor(sources: readonly Getter<T>[]) {
		this.#sources = sources;
	}

	peek(): Awaited<T> | undefined {
		return this.#result?.ok ? (this.#result.value as Awaited<T>) : undefined;
	}

	changed(): Promise<Awaited<T> | undefined>;
	changed(fn: Subscriber<Awaited<T> | undefined>): Dispose;
	changed(fn?: Subscriber<Awaited<T> | undefined>): Promise<Awaited<T> | undefined> | Dispose {
		return fn ? this.subscribe(fn) : new Promise((resolve) => this.subscribe(resolve));
	}

	subscribe(fn: Subscriber<Awaited<T> | undefined>): Dispose {
		return this.#watch((result) => {
			if (result.ok) fn(result.value as Awaited<T>);
		});
	}

	#watch(fn: Listener): Dispose {
		if (this.#result) return noop;
		this.#listeners.add(fn);
		if (!this.#won && this.#listeners.size === 1) {
			try {
				for (const source of this.#sources) {
					this.#dispose.push(source.changed((value) => this.#finish(value)));
				}
			} catch (error) {
				this.#listeners.delete(fn);
				this.#stop();
				throw error;
			}
		}
		return () => {
			this.#listeners.delete(fn);
			if (!this.#listeners.size) this.#stop();
		};
	}

	[CHANGE_LISTEN](fn: Listener): Dispose {
		if (this.#result) {
			fn(this.#result);
			return noop;
		}
		return this.#watch(fn);
	}

	#finish(value: T): void {
		if (this.#won) return;
		this.#won = true;
		this.#stop();
		this.#sources = [];
		Promise.resolve(value).then(
			(value) => this.#resolve({ ok: true, value }),
			(error: unknown) => this.#resolve({ ok: false, error }),
		);
	}

	#resolve(result: Settled): void {
		this.#result = result;
		const listeners = [...this.#listeners];
		this.#listeners.clear();
		for (const listener of listeners) {
			try {
				listener(result);
			} catch (error) {
				console.error("signal changed error", error);
			}
		}
	}

	#stop(): void {
		for (const dispose of this.#dispose) dispose();
		this.#dispose = [];
	}

	// Await reads `then` synchronously before scheduling its call. Start here so a
	// queued source notification cannot slip between the caller's check and await.
	// biome-ignore lint/suspicious/noThenProperty: The change wait is intentionally awaitable.
	get then(): Promise<Awaited<T>>["then"] {
		const promise = new Promise<Awaited<T>>((resolve, reject) => {
			this[CHANGE_LISTEN]((result) => {
				if (result.ok) resolve(result.value as Awaited<T>);
				else reject(result.error);
			});
		});
		return promise.then.bind(promise);
	}
}

/**
 * A value that settles exactly once, then never changes: both **observable** and **awaitable**.
 *
 * Read it reactively like a {@link Getter} (`peek()` / `changed()` / `subscribe()` / `effect.get()`;
 * the value is `undefined` while pending), or await it like a promise (`await once` /
 * `once.then(...)` resolve with the settled value, immediately if it already settled). This is the
 * shape of terminal state such as "closed": one handle serves the sync check, the reactive
 * short-circuit, and the `await`.
 *
 * Settle it with {@link set} exactly once; a second call throws. Expose it to callers as
 * {@link GetPromise} so they can observe/await but not settle it. `T` must not include `undefined`
 * (that is the pending sentinel).
 */
export class Once<T> implements GetPromise<T> {
	#signal = new Signal<T | undefined>(undefined);

	/** Marks this as a readable across package instances. */
	readonly [GETTER_BRAND] = true;

	/** Settle the value. Throws if it has already settled. */
	set(value: T): void {
		if (this.#signal.peek() !== undefined) {
			throw new Error("Once has already settled");
		}
		this.#signal.set(value);
	}

	/** The settled value, or `undefined` while still pending. */
	peek(): T | undefined {
		return this.#signal.peek();
	}

	/** Resolves when it settles. */
	changed(): Promise<T | undefined>;
	/** Calls `fn` once when it settles. Returns a function to cancel. */
	changed(fn: (value: T | undefined) => void): Dispose;
	changed(fn?: (value: T | undefined) => void): Promise<T | undefined> | Dispose {
		return fn ? this.#signal.changed(fn) : this.#signal.changed();
	}

	/** Calls `fn` when it settles (fires at most once). Returns a function to unsubscribe. */
	subscribe(fn: (value: T | undefined) => void): Dispose {
		return this.#signal.subscribe(fn);
	}

	/** Resolves with the settled value, immediately if it already settled. Never rejects on its own. */
	// biome-ignore lint/suspicious/noThenProperty: Once is intentionally awaitable (thenable).
	then<R1 = T, R2 = never>(
		onFulfilled?: ((value: T) => R1 | PromiseLike<R1>) | null,
		onRejected?: ((reason: unknown) => R2 | PromiseLike<R2>) | null,
	): PromiseLike<R1 | R2> {
		const current = this.#signal.peek();
		const settled: Promise<T> =
			current !== undefined ? Promise.resolve(current) : this.#signal.changed().then((value) => value as T);
		return settled.then(onFulfilled, onRejected);
	}
}

// Every promise a race has watched, mapped to the listeners of the races still waiting on it. A
// native reaction can never be removed, so each promise gets exactly one, shared by every race:
// racing a long-lived promise per frame then costs a removable listener, not a reaction per call.
type Settled = { ok: true; value: unknown } | { ok: false; error: unknown };
type Listener = (settled: Settled) => void;
type Watched = { settled?: Settled; listeners: Set<Listener> };
const watched = new WeakMap<object, Watched>();

function isThenable(value: unknown): value is PromiseLike<unknown> & object {
	return (
		(typeof value === "object" || typeof value === "function") &&
		value !== null &&
		typeof (value as PromiseLike<unknown>).then === "function"
	);
}

// Calls `fn` once when `value` settles, synchronously if it already has. Returns a disposer.
function listen(value: unknown, fn: Listener): Dispose {
	if (branded(value, CHANGE_LISTEN)) {
		return (value as Changed<unknown>)[CHANGE_LISTEN](fn);
	}

	if (!isThenable(value)) {
		fn({ ok: true, value });
		return noop;
	}

	if (getterShaped(value)) {
		const readable = value as GetPromise<unknown>;
		const current = readable.peek();
		if (current !== undefined) {
			fn({ ok: true, value: current });
			return noop;
		}

		const dispose = readable.subscribe((next) => {
			if (next === undefined) return;
			dispose();
			fn({ ok: true, value: next });
		});
		return dispose;
	}

	let entry = watched.get(value);
	if (!entry) {
		const created: Watched = { listeners: new Set() };
		Promise.resolve(value).then(
			(value) => settle(created, { ok: true, value }),
			(error: unknown) => settle(created, { ok: false, error }),
		);
		watched.set(value, created);
		entry = created;
	}

	if (entry.settled) {
		fn(entry.settled);
		return noop;
	}

	const listeners = entry.listeners;
	listeners.add(fn);
	return () => listeners.delete(fn);
}

function settle(entry: Watched, settled: Settled): void {
	entry.settled = settled;
	const listeners = [...entry.listeners];
	entry.listeners.clear();
	for (const fn of listeners) fn(settled);
}

// Settles with the first of `values`, or `undefined` once `abort` fires, then drops every listener.
function raceUntil<T>(values: readonly unknown[], abort?: AbortSignal): Promise<T> {
	return new Promise<T>((resolve, reject) => {
		const disposes: Dispose[] = [];
		let done = false;
		const finish = (settled: Settled) => {
			if (done) return;
			done = true;
			for (const dispose of disposes) dispose();
			if (settled.ok) resolve(settled.value as T);
			else reject(settled.error);
		};

		if (abort?.aborted) {
			finish({ ok: true, value: undefined });
		} else if (abort) {
			const stop = () => finish({ ok: true, value: undefined });
			abort.addEventListener("abort", stop);
			disposes.push(() => abort.removeEventListener("abort", stop));
		}

		for (const value of values) {
			// Observe a loser even once the race is decided, as Promise.race does, so its later
			// rejection is not reported as unhandled. A thenable keeps one shared reaction.
			if (done) {
				listen(value, noop)();
				continue;
			}
			const dispose = listen(value, finish);
			if (done) dispose();
			else disposes.push(dispose);
		}
	});
}

/**
 * Settles with the first of `values` to settle, like `Promise.race`, then drops every listener it
 * registered.
 *
 * Accepts promises and {@link GetPromise} values such as a {@link Once}. Racing a value that
 * outlives the call (a `closed` pending for the whole track) once per frame therefore leaks nothing,
 * where `Promise.race` would leave a listener or reaction behind on every call. An already settled
 * value wins at once. Like `Promise.race`, every value is observed, so a loser that rejects later
 * is not an unhandled rejection.
 */
export function race<const T extends readonly unknown[]>(values: T): Promise<Awaited<T[number]>> {
	return raceUntil(values);
}

/** The value type a {@link Setter} accepts, e.g. `number` for `Signal<number>`. */
export type SetterType<S> = S extends Setter<infer T> ? T : never;

/** The value type a {@link Getter} yields, e.g. `number` for `Getter<number>`. */
export type GetterType<G> = G extends Getter<infer T> ? T : never;

/** A record of named signals, used to group a component's `in` or `out` signals. */
export type SignalMap = Record<string, Getter<unknown>>;

/**
 * A read-only view over a {@link SignalMap}: every entry collapses to its {@link Getter} and the
 * record itself is readonly. Consumers can peek/subscribe but can neither call `set()`
 * nor swap a signal out, so the owning component keeps sole write access.
 */
export type Readonlys<T extends SignalMap> = {
	readonly [K in keyof T]: Getter<GetterType<T[K]>>;
};

/**
 * Re-types a record of Signals as read-only {@link Getter}s. This is the identity function at
 * runtime; it only narrows the static type. Keep the original (writable) reference
 * private for the component to set, and expose the result as the public `out`.
 *
 * ```ts
 * readonly #out = { status: new Signal("offline") };
 * readonly out = readonlys(this.#out); // status is now a Getter to callers
 * ```
 */
export function readonlys<T extends SignalMap>(signals: T): Readonlys<T> {
	return signals as unknown as Readonlys<T>;
}

/**
 * A value or an existing readable for it: the argument form accepted by {@link getter}
 * and, per-field, by {@link Inputs}. Mirrors the `T | Signal<T>` shape of {@link Signal.from}.
 *
 * A readable is any {@link Getter}: a `Signal`, `Computed`, `Once`, or {@link Derived}
 * from this package (including another copy's branded `Signal`), a {@link readonlys}
 * output, or a foreign adapter with the same three methods. {@link getter} reuses those
 * as-is and wraps any other value in a fresh `Signal`. Shape is the discriminator, not a
 * brand: an object whose `peek`, `subscribe`, and `changed` are functions is a readable,
 * including data that happens to carry those three functions. A plain object with a
 * `peek` data field is not.
 */
export type GetterInit<T> = T | Getter<T>;

/**
 * Builds a read-only {@link Getter} from a {@link GetterInit}: any existing {@link Getter}
 * is reused as-is, and any other value is wrapped in a fresh `Signal`.
 *
 * Does not wrap a readable or subscribe to it. Wrapping would freeze it into a constant;
 * a subscription here would leak because nothing owns it. Older copies of this package
 * brand `Signal` but not the readable; those still pass through. Runtime reuse is by
 * duck type: an object whose `peek`, `subscribe`, and `changed` are functions is treated
 * as a {@link Getter}, even if it is data that happens to carry those names. There is no
 * extra heuristic.
 *
 * ```ts
 * getter(1);
 * getter(new Signal(1));
 * getter(new Computed((e) => e.get(source)));
 * getter(new Once<string>());
 * getter(new Derived([source], (v) => v > 0));
 * getter(produced.out.count);
 * getter(adapter); // any object with peek, subscribe, and changed
 * ```
 */
export function getter<T>(value: GetterInit<T>): Getter<T> {
	if (branded(value, GETTER_BRAND) || branded(value, SIGNAL_BRAND) || getterShaped(value)) {
		return value as Getter<T>;
	}

	return new Signal(value as T);
}

// Shape, not brand: a foreign adapter and an older unbranded readable both match.
// Wrapping either would freeze it into a constant. Data that happens to carry
// peek/subscribe/changed functions matches too; we do not try to tell it from a Getter.
function getterShaped(value: unknown): boolean {
	if (typeof value !== "object" || value === null) return false;
	const maybe = value as Partial<Getter<unknown>>;
	return (
		typeof maybe.peek === "function" && typeof maybe.subscribe === "function" && typeof maybe.changed === "function"
	);
}

/**
 * Derives a component's constructor argument from its `in` map: every entry becomes
 * optional and accepts a {@link GetterInit} of that field's value (the {@link getter}
 * contract). Removes the hand-written, drift-prone argument interface.
 *
 * ```ts
 * type In = { count: Getter<number> };
 * constructor(props?: Inputs<In>) {
 *   this.in = { count: getter(props?.count ?? 0) };
 * }
 * new C({ count: 1 });
 * new C({ count: new Signal(1) });
 * new C({ count: other.out.count });
 * new C({ count: adapter });
 * ```
 */
export type Inputs<I extends SignalMap> = { [K in keyof I]?: GetterInit<GetterType<I[K]>> };

/**
 * Runs a function that reads signals via `effect.get(...)` and reruns whenever
 * any of them change. Registers cleanup, timers, and event listeners that are
 * torn down automatically on each rerun and when the effect is closed.
 */
// TODO Make this a single instance of an Effect, so close() can work correctly from async code.
export class Effect {
	// Catches effects that are garbage collected without being closed.
	static #finalizer = new FinalizationRegistry<Error>((origin) => {
		console.warn(`Signals was garbage collected without being closed:\n${origin.stack}`);
	});

	#fn?: (effect: Effect) => void;
	#dispose?: Dispose[] = [];

	// The teardown list a drain is popping. Shared by the rerun and close paths so a `close`
	// landing inside a rerun's drain continues that pass instead of starting a second one. Set
	// only while a drain is running.
	#draining?: Dispose[];
	#unwatch: Dispose[] = [];
	#async = new Set<Promise<void>>();

	// Captured eagerly but formatted only when a diagnostic prints it, which keeps creation cheap.
	#origin = new Error("created here:");
	get #stack(): string | undefined {
		return this.#origin.stack;
	}
	#scheduled = false;

	#closed: PromiseWithResolvers<void>;

	#abort: AbortController = new AbortController();
	#abortUsed = false;

	// True between a run's teardown and the start of the next one. A spawn task that resumes in
	// this window belongs to the run that just died, so its cleanup has to fire now.
	#stale = false;

	/** If a function is provided, it runs immediately and reruns whenever a tracked signal changes. */
	constructor(fn?: (effect: Effect) => void) {
		Effect.#finalizer.register(this, this.#origin, this);

		this.#fn = fn;

		this.#closed = Promise.withResolvers();

		if (fn) {
			this.#schedule();
		}
	}

	#schedule(): void {
		if (this.#scheduled) return;
		this.#scheduled = true;

		// We always queue a microtask to make it more difficult to get stuck in an infinite loop.
		queueMicrotask(() =>
			this.#run().catch((error) => {
				console.error("effect error", error, this.#stack);
			}),
		);
	}

	async #run(): Promise<void> {
		if (this.#dispose === undefined) return; // closed, no error because this is a microtask

		// The next run's scope is created when that run starts, not here, so a spawn task
		// resuming in between still sees its own run's state: aborted.
		this.#stale = true;
		this.#abort.abort();

		// Unsubscribe from all signals.
		for (const unwatch of this.#unwatch) unwatch();
		this.#unwatch.length = 0;

		// Run the cleanup functions for the previous run.
		const dispose = this.#dispose;
		this.#drain(dispose);

		// A cleanup may have closed the effect, which drained the rest of the list and finished
		// teardown already. There is no next run to open.
		if (this.#dispose === undefined) return;

		// Wait for every task this run spawned. Opening the next run while one is still in flight
		// would hand that task's cleanup registrations, and its `abort` signal, to a run it never
		// belonged to. The dispose functions above already closed whatever the task was awaiting,
		// so anything that observes cancellation unwinds from here.
		if (this.#async.size > 0) {
			// Diagnostic only: a task that ignores cancellation stalls the rerun, so name it.
			const warn = new AbortController();
			sleep(Nano.fromSecond(Second(5)), { signal: warn.signal, ref: false }).then(() => {
				// A close() racing the timer disarms it, but the wakeup can already be
				// queued by then. There is nothing to warn about once the effect is gone.
				if (this.#dispose === undefined) return;

				console.warn("spawn is still running after 5s; the effect cannot rerun until it settles", this.#stack);
			}, noop);

			try {
				// A task can spawn another as it unwinds, so drain until nothing new is queued.
				while (this.#dispose !== undefined && this.#async.size > 0) {
					const pending = this.#async;
					this.#async = new Set();

					// close() has to release the wait rather than wait behind it. It already ran
					// every dispose function, and there is no next run left to protect, so a task
					// that never settles must not pin this loop (or the timer below) forever.
					await race([Promise.all(pending), this.#closed.promise]);
				}
			} catch (error) {
				console.error("async effect error", error);
				if (this.#stack) console.error("stack", this.#stack);
			} finally {
				warn.abort();
			}
		}

		// We were closed while waiting for async effects to complete.
		if (this.#dispose === undefined) return;

		// IMPORTANT: must run all of the dispose functions before unscheduling.
		// Otherwise, cleanup functions could get us stuck in an infinite loop.
		this.#scheduled = false;

		// Open this run's scope. Anything still holding the previous one keeps seeing it torn down.
		this.#stale = false;
		this.#abort = new AbortController();
		this.#abortUsed = false;

		if (this.#fn) {
			this.#fn(this);

			if (
				this.#dispose !== undefined &&
				this.#unwatch.length === 0 &&
				this.#dispose.length === 0 &&
				this.#async.size === 0 &&
				!this.#abortUsed
			) {
				console.warn("Effect did not subscribe to any signals; it will never rerun.", this.#stack);
			}
		}
	}

	/** Reads a signal and tracks it, rerunning the effect whenever it changes. */
	get<T>(signal: Getter<T>): T {
		if (this.#dispose === undefined) {
			console.warn("Effect.get called when closed, returning current value");
			return signal.peek();
		}

		const value = signal.peek();

		// NOTE: We use changed instead of subscribe just so it's slightly more efficient.
		// 1 clear() instead of N delete() calls.
		const dispose = signal.changed(() => this.#schedule());
		this.#unwatch.push(dispose);

		return value;
	}

	/**
	 * Sets a signal for the duration of this run, restoring `cleanup` on rerun or close.
	 * The cleanup value is optional only when the signal type includes `undefined`.
	 */
	set<S extends Setter<unknown>>(
		signal: S,
		value: SetterType<S>,
		...args: undefined extends SetterType<S> ? [cleanup?: SetterType<S>] : [cleanup: SetterType<S>]
	): void {
		if (this.#dispose === undefined) {
			console.warn("Effect.set called when closed, ignoring");
			return;
		}

		signal.set(value);
		const cleanup = args[0];
		const cleanupValue = cleanup === undefined ? (undefined as SetterType<S>) : cleanup;
		this.cleanup(() => signal.set(cleanupValue));
	}

	/**
	 * Runs an async task. The effect will not rerun until the task's promise settles.
	 */
	// TODO: Add effect for another layer of nesting
	spawn(fn: () => Promise<void>) {
		if (this.#dispose === undefined) {
			console.warn("Effect.spawn called when closed");

			return;
		}

		const promise = fn().catch((error) => {
			console.error("spawn error", error);
		});

		// `fn` can close the effect before it first awaits. `close()` empties `#async` and only the
		// rerun path drains it, so pushing now would pin the task on an effect that can never rerun.
		if (this.#dispose === undefined) return;

		// A settled task has nothing left for a rerun to wait on. Dropping it matters for an effect
		// that never reruns (`new Effect()` spawning per group), which would otherwise keep every one.
		// The set, not `this`, so a task that never settles cannot pin a closed effect.
		const tasks = this.#async;
		tasks.add(promise);
		void promise.then(() => tasks.delete(promise));
	}

	/** Runs `fn` after `duration` on the installed clock, unless the effect reruns or closes first. */
	timer(fn: () => void, duration: Nano) {
		if (this.#dispose === undefined) {
			console.warn("Effect.timer called when closed, ignoring");
			return;
		}

		const abort = new AbortController();
		// The wakeup can already be queued when cleanup aborts, so check again before running.
		void sleep(duration, { signal: abort.signal }).then(() => {
			if (!abort.signal.aborted) fn();
		}, noop);
		this.cleanup(() => abort.abort());
	}

	/**
	 * Runs `fn` as a nested effect, then closes that effect after `duration` on the installed clock.
	 *
	 * Shares {@link run}'s handling of a task that outlived its run: the child is closed at the
	 * next teardown, not immediately.
	 */
	timeout(fn: (effect: Effect) => void, duration: Nano) {
		if (this.#dispose === undefined) {
			console.warn("Effect.timeout called when closed, ignoring");
			return;
		}

		const effect = new Effect(fn);
		const abort = new AbortController();
		void sleep(duration, { signal: abort.signal }).then(() => effect.close(), noop);

		this.#dispose.push(() => {
			abort.abort();
			effect.close();
		});
	}

	/** Runs `fn` on the next animation frame, unless the effect reruns or closes first. */
	animate(fn: (now: DOMHighResTimeStamp) => void) {
		if (this.#dispose === undefined) {
			console.warn("Effect.animate called when closed, ignoring");
			return;
		}

		let animate: number | undefined = requestAnimationFrame((now) => {
			fn(now);
			animate = undefined;
		});
		this.cleanup(() => {
			if (animate !== undefined) cancelAnimationFrame(animate);
		});
	}

	/**
	 * Runs `fn` every positive period until cleanup, arming the first deadline before returning.
	 * Each period uses the clock installed then; a late callback delays the next by a full period.
	 */
	interval(fn: () => void, period: Nano) {
		if (this.#dispose === undefined) {
			console.warn("Effect.interval called when closed, ignoring");
			return;
		}

		Instant.add(Instant.zero, period);
		if (period === 0) throw new RangeError("interval period must be positive");
		const abort = new AbortController();
		let next = Instant.add(now(), period);
		void (async () => {
			for (;;) {
				await sleepUntil(next, { signal: abort.signal });
				if (abort.signal.aborted) return;
				try {
					fn();
				} catch (error) {
					console.error("effect interval error", error, this.#stack);
				}
				// A callback may install a clock with a different origin. A full Delay period
				// starts from that clock's now, never from the previous clock's deadline.
				next = Instant.add(now(), period);
			}
		})().catch(noop);
		this.cleanup(() => abort.abort());
	}

	/**
	 * Creates a nested effect that reruns independently and is closed with its parent.
	 *
	 * Returns a disposer that closes the child early and releases it from the parent, so a long-lived
	 * effect spawning a child per event (e.g. one per accepted subscription) doesn't accumulate dead
	 * scopes until it finally reruns or closes.
	 *
	 * Called from a task that outlived its run, the child is closed at the *next* teardown rather
	 * than immediately, unlike {@link cleanup}. Closing it now would cancel its first run before
	 * `fn` executes, so whatever teardown `fn` registers would never fire at all.
	 */
	run(fn: (effect: Effect) => void): Dispose {
		if (this.#dispose === undefined) {
			console.warn("Effect.run called when closed, ignoring");
			return () => {};
		}

		const effect = new Effect(fn);
		const dispose = () => effect.close();
		this.#dispose.push(dispose);

		return () => {
			effect.close();

			// Drop our disposer from the parent so repeated run()/dispose() cycles don't pile up.
			// Safe mid-drain too: the drain pops from the end, so every entry left is still pending.
			const disposers = this.#dispose;
			const index = disposers?.indexOf(dispose) ?? -1;
			if (disposers !== undefined && index !== -1) disposers.splice(index, 1);
		};
	}

	/** Creates a derived signal scoped to this effect, closed when the effect reruns or closes. */
	computed<T>(fn: (effect: Effect) => T): Computed<T> {
		const computed = new Computed(fn);
		this.cleanup(() => computed.close());
		return computed;
	}

	/** Reads and tracks several signals, returning their values or `undefined` if any is falsy. */
	getAll<S extends readonly Getter<unknown>[]>(
		signals: [...S],
	): { [K in keyof S]: Exclude<GetterType<S[K]>, false | 0 | "" | null | undefined> } | undefined {
		const values: unknown[] = [];
		for (const signal of signals) {
			const value = this.get(signal);
			if (!value) return undefined;
			values.push(value);
		}
		return values as { [K in keyof S]: Exclude<GetterType<S[K]>, false | 0 | "" | null | undefined> };
	}

	/** Runs `fn` with the signal's value now and again whenever it changes, scoped to this effect. */
	subscribe<T>(signal: Getter<T>, fn: (value: T) => void) {
		if (this.#dispose === undefined) {
			console.warn("Effect.subscribe called when closed, running once");
			fn(signal.peek());
			return;
		}

		this.run((effect) => {
			const value = effect.get(signal);
			fn(value);
		});
	}

	/** Adds an {@link HTMLElement} listener, removed automatically when the effect reruns or closes. */
	event<K extends keyof HTMLElementEventMap>(
		target: HTMLElement,
		type: K,
		listener: (this: HTMLElement, ev: HTMLElementEventMap[K]) => void,
		options?: boolean | AddEventListenerOptions,
	): void;
	/** Adds a typed SVGElement listener, removed when the effect reruns or closes. */
	event<K extends keyof SVGElementEventMap>(
		target: SVGElement,
		type: K,
		listener: (this: SVGElement, ev: SVGElementEventMap[K]) => void,
		options?: boolean | AddEventListenerOptions,
	): void;
	/** Adds a typed Document listener, removed when the effect reruns or closes. */
	event<K extends keyof DocumentEventMap>(
		target: Document,
		type: K,
		listener: (this: Document, ev: DocumentEventMap[K]) => void,
		options?: boolean | AddEventListenerOptions,
	): void;
	/** Adds a typed Window listener, removed when the effect reruns or closes. */
	event<K extends keyof WindowEventMap>(
		target: Window,
		type: K,
		listener: (this: Window, ev: WindowEventMap[K]) => void,
		options?: boolean | AddEventListenerOptions,
	): void;
	/** Adds a typed WebSocket listener, removed when the effect reruns or closes. */
	event<K extends keyof WebSocketEventMap>(
		target: WebSocket,
		type: K,
		listener: (this: WebSocket, ev: WebSocketEventMap[K]) => void,
		options?: boolean | AddEventListenerOptions,
	): void;
	/** Adds a typed XMLHttpRequest listener, removed when the effect reruns or closes. */
	event<K extends keyof XMLHttpRequestEventMap>(
		target: XMLHttpRequest,
		type: K,
		listener: (this: XMLHttpRequest, ev: XMLHttpRequestEventMap[K]) => void,
		options?: boolean | AddEventListenerOptions,
	): void;
	/** Adds a typed MediaQueryList listener, removed when the effect reruns or closes. */
	event<K extends keyof MediaQueryListEventMap>(
		target: MediaQueryList,
		type: K,
		listener: (this: MediaQueryList, ev: MediaQueryListEventMap[K]) => void,
		options?: boolean | AddEventListenerOptions,
	): void;
	/** Adds a typed Animation listener, removed when the effect reruns or closes. */
	event<K extends keyof AnimationEventMap>(
		target: Animation,
		type: K,
		listener: (this: Animation, ev: AnimationEventMap[K]) => void,
		options?: boolean | AddEventListenerOptions,
	): void;
	/** Adds a typed EventSource listener, removed when the effect reruns or closes. */
	event<K extends keyof EventSourceEventMap>(
		target: EventSource,
		type: K,
		listener: (this: EventSource, ev: EventSourceEventMap[K]) => void,
		options?: boolean | AddEventListenerOptions,
	): void;
	/** Adds a listener for any event on any target, removed when the effect reruns or closes. */
	event(
		target: EventTarget,
		type: string,
		listener: EventListenerOrEventListenerObject,
		options?: boolean | AddEventListenerOptions,
	): void;
	event(
		target: EventTarget,
		type: string,
		listener: EventListenerOrEventListenerObject,
		options?: boolean | AddEventListenerOptions,
	): void {
		if (this.#dispose === undefined) {
			console.warn("Effect.eventListener called when closed, ignoring");
			return;
		}

		// Merge the abort signal so the listener is auto-removed on rerun/close.
		const effectSignal = this.abort;
		const signal =
			typeof options !== "boolean" && options?.signal
				? AbortSignal.any([effectSignal, options.signal])
				: effectSignal;
		const merged: AddEventListenerOptions =
			typeof options === "boolean" ? { capture: options, signal } : { ...options, signal };

		target.addEventListener(type, listener, merged);
	}

	/**
	 * Pop and run every callback in `list` exactly once, newest first. One registered while
	 * draining is the newest, so it runs next.
	 *
	 * Iterative, so a cascade of any depth stays flat rather than nesting a stack frame per link.
	 * Re-entrant: a `close` from inside a rerun's teardown keeps popping the same list instead of
	 * replaying it. A callback that throws is reported and the rest still run, since teardown that
	 * stops half way leaks whatever the remaining callbacks owned.
	 */
	#drain(list: Dispose[]): void {
		const outer = this.#draining;
		this.#draining = list;

		try {
			for (let fn = list.pop(); fn !== undefined; fn = list.pop()) {
				try {
					fn();
				} catch (error) {
					console.error("cleanup error", error, this.#stack);
				}
			}
		} finally {
			this.#draining = outer;
		}
	}

	/**
	 * Registers a function to run when the effect reruns or closes.
	 *
	 * Teardown runs last-in, first-out, like `DisposableStack` or Rust's drop order, so something
	 * registered later (a nested {@link run} built on an earlier resource) is released before what
	 * it depends on. Every scoped helper registers here too: {@link set}, {@link run},
	 * {@link computed}, and the timers.
	 *
	 * Runs `fn` immediately if the run that registered it is already over, which is what an
	 * {@link spawn} task resuming after a rerun or close sees. Registering teardown is
	 * therefore enough to own a resource, with no staleness check needed first.
	 */
	cleanup(fn: Dispose): void {
		if (this.#dispose === undefined || this.#stale) {
			// Teardown that cascades joins the drain in progress rather than running nested inside
			// the callback that registered it, which would turn a long chain into a stack overflow.
			if (this.#draining !== undefined) {
				this.#draining.push(fn);
				return;
			}

			fn();
			return;
		}

		this.#dispose.push(fn);
	}

	/** Stops the effect permanently, running all cleanup and unsubscribing from every signal. */
	close(): void {
		const dispose = this.#dispose;
		if (dispose === undefined) {
			return;
		}

		// Mark closed before running teardown. A cleanup that calls back into this effect has to
		// hit the closed guard: otherwise `spawn`/`timer`/`animate` register work against a list
		// this call is about to discard, and the task keeps running with nothing tracking it.
		this.#dispose = undefined;

		this.#closed.resolve();
		this.#abort.abort();

		this.#drain(dispose);

		for (const signal of this.#unwatch) signal();
		this.#unwatch.length = 0;

		this.#async.clear();

		Effect.#finalizer.unregister(this);
	}

	/** Resolves when the effect is closed. */
	get closed(): Promise<void> {
		return this.#closed.promise;
	}

	/**
	 * Settles with the first of `values`, like the free {@link race}, or resolves `undefined` once the
	 * current run is torn down. Either way it drops every listener it registered.
	 */
	race<const T extends readonly unknown[]>(...values: T): Promise<Awaited<T[number]> | undefined> {
		return raceUntil(values, this.#abort.signal);
	}

	/** An AbortSignal that fires when the current run is torn down. */
	get abort(): AbortSignal {
		this.#abortUsed = true;
		return this.#abort.signal;
	}

	/** Copies `src` into `dst` and keeps `dst` in sync as `src` changes. */
	proxy<T>(dst: Setter<T>, src: Getter<T>): void {
		this.subscribe(src, (value) => dst.update(() => value));
	}
}

/**
 * A read-only signal derived from other signals.
 *
 * The compute function reads its dependencies with `effect.get(...)`, exactly
 * like an effect, and returns the derived value. It reruns whenever a
 * dependency changes. Keep it pure: derive a value, don't perform side effects.
 *
 * Like every signal, updates are asynchronous: the value is `undefined` until
 * the first run completes (and after close()), and recomputes propagate on a
 * microtask. Read it inside an effect and handle the `undefined` case, the same
 * way you would any other signal that starts empty.
 */
export class Computed<T> implements Getter<T | undefined> {
	#signal = new Signal<T | undefined>(undefined);
	#effect: Effect;

	/** Marks this as a readable across package instances. */
	readonly [GETTER_BRAND] = true;

	/** Creates a computed that derives its value from `fn`, rerunning when dependencies change. */
	constructor(fn: (effect: Effect) => T) {
		this.#effect = new Effect((effect) => {
			this.#signal.set(fn(effect));
		});
	}

	/** Returns the current derived value without subscribing (`undefined` until the first run). */
	peek(): T | undefined {
		return this.#signal.peek();
	}

	/** Resolves the next time the derived value changes. */
	changed(): Promise<T | undefined>;
	/** Calls `fn` once the next time the derived value changes. Returns a function to cancel. */
	changed(fn: Subscriber<T | undefined>): Dispose;
	changed(fn?: Subscriber<T | undefined>): Promise<T | undefined> | Dispose {
		return fn ? this.#signal.changed(fn) : this.#signal.changed();
	}

	/** Calls `fn` every time the derived value changes. */
	subscribe(fn: Subscriber<T | undefined>): Dispose {
		return this.#signal.subscribe(fn);
	}

	/**
	 * Stops recomputing and tracking dependencies. Required for standalone computeds;
	 * an `effect.computed()` is closed automatically with its parent effect.
	 */
	close(): void {
		this.#effect.close();
		this.#signal.set(undefined);
	}
}

/** The values behind a tuple of readables, positionally. */
export type GetterValues<S extends readonly Getter<unknown>[]> = { [K in keyof S]: GetterType<S[K]> };

/**
 * A read-only view over other readables, recomputed on every read.
 *
 * The lifecycle-free counterpart to {@link Computed}: it holds no {@link Effect} and needs no
 * `close()`, its value is correct from the first `peek()` rather than `undefined` until a first
 * run, and it reads synchronously. In exchange the sources are named up front instead of
 * tracked automatically, and `fn` must be cheap and pure since it runs per read.
 *
 * Reach for it when a class wants to publish a small mapped view of its own state as part of
 * its public surface. A hand-written object with the same three methods also satisfies
 * {@link Getter} and is accepted by {@link getter}; this class is the packaged form of that
 * view, with sources named up front.
 *
 * It relays its sources rather than filtering them: every source notification becomes one
 * here, even when the mapped value is unchanged. Deduplicating instead would have to compare
 * against the value at subscribe time, which silently swallows two real edges: a source that
 * already applied a change whose flush is still queued, and an in-place {@link Signal.mutate}
 * of a value the mapping returns as-is. A redundant rerun is the cheaper failure.
 *
 * ```ts
 * readonly online = new Derived([this.#peers], (peers) => peers.size > 0);
 * ```
 */
export class Derived<const S extends readonly Getter<unknown>[], T> implements Getter<T> {
	#sources: S;
	#fn: (...values: GetterValues<S>) => T;

	/** Marks this as a readable across package instances. */
	readonly [GETTER_BRAND] = true;

	/** Creates a view deriving its value from `sources` via `fn`, recomputed on every read. */
	constructor(sources: S, fn: (...values: GetterValues<S>) => T) {
		this.#sources = sources;
		this.#fn = fn;
	}

	/** Returns the current derived value without subscribing. */
	peek(): T {
		return this.#fn(...(this.#sources.map((source) => source.peek()) as unknown as GetterValues<S>));
	}

	/** Calls `fn` with the derived value every time any source notifies. */
	subscribe(fn: Subscriber<T>): Dispose {
		return this.#watch(fn);
	}

	/** Resolves the next time the derived value changes. */
	changed(): Promise<T>;
	/** Calls `fn` once the next time the derived value changes. Returns a function to cancel. */
	changed(fn: Subscriber<T>): Dispose;
	changed(fn?: Subscriber<T>): Promise<T> | Dispose {
		if (fn) return this.#once(fn);

		return new Promise<T>((resolve) => {
			this.#once(resolve);
		});
	}

	// Calls `fn` with the derived value the first time it changes, then unsubscribes.
	#once(fn: Subscriber<T>): Dispose {
		const dispose = this.subscribe((value) => {
			dispose();
			fn(value);
		});
		return dispose;
	}

	// Subscribes to every source, recomputing on any notification.
	#watch(fn: Subscriber<T>): Dispose {
		const disposes = this.#sources.map((source) => source.subscribe(() => fn(this.peek())));
		return () => {
			for (const dispose of disposes) dispose();
		};
	}
}

// Deep equality for plain objects/arrays, SameValueZero for everything else.
// Class instances have identity semantics (e.g. two different Broadcast instances are never equal).
function isEqual(a: unknown, b: unknown): boolean {
	if (a === b || (Number.isNaN(a) && Number.isNaN(b))) return true;
	if (a === null || b === null || typeof a !== "object" || typeof b !== "object") return false;

	const protoA = Object.getPrototypeOf(a);
	const protoB = Object.getPrototypeOf(b);

	// Both must be plain objects or both arrays to deep-compare.
	if (protoA !== protoB) return false;
	if (protoA !== Object.prototype && protoA !== Array.prototype) return false;

	// Holes are not keys, so a sparse array's length is only visible here.
	if (protoA === Array.prototype && (a as unknown[]).length !== (b as unknown[]).length) return false;

	const keysA = Object.keys(a);
	if (keysA.length !== Object.keys(b).length) return false;

	for (const key of keysA) {
		// Membership matters on its own: `{ a: undefined }` is not `{ b: undefined }`.
		if (!Object.hasOwn(b, key)) return false;
		if (!isEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])) return false;
	}

	return true;
}
